import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

/** Merge Tailwind classes safely (shadcn convention). */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/** Format a BigDecimal-like string the API returned. Never use Number for money. */
export function formatAmount(
  value: string | number,
  currency = 'MAD',
  locale = 'fr-MA',
): string {
  const asString = typeof value === 'number' ? value.toString() : value;
  // Keep precision: use Intl with the string parsed via Number only for display.
  // For totals or aggregations, ALWAYS use BigDecimal on the server side.
  const n = Number(asString);
  if (!Number.isFinite(n)) return asString;
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

/** Generate a UUID v4 — used for X-Request-ID idempotency keys. */
export function uuidv4(): string {
  // Access the Web Crypto API via globalThis to avoid the type conflict between
  // @types/node (which types `crypto` as the Node module) and the browser Crypto
  // interface. Casting through globalThis gives us the correct Crypto type.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const webCrypto = (globalThis as any).crypto as Crypto | undefined;

  if (webCrypto?.randomUUID) {
    return webCrypto.randomUUID();
  }
  if (webCrypto?.getRandomValues) {
    const bytes = new Uint8Array(16);
    webCrypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }
  // Last-resort Math.random fallback (non-cryptographic).
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
